namespace Newtonsoft.ViewModels.JsonSchema {
    export class ValidatorViewModel {
        schemaText = ko.observable<string>();
        jsonText = ko.observable<string>();
        loadingSchema = ko.observable<boolean>();
        savingPage = ko.observable<boolean>();
        savingPageFailedMessage = ko.observable<string>();
        savingPageSuccessMessage = ko.observable<string>();
        savedUrl = ko.observable<string>();
        validation: any;
        schemaFailedMessage = ko.observable<string>();
        jsonFailedMessage = ko.observable<string>();
        jsonSuccessMessage = ko.observable<string>();
        schemaValidationErrors = ko.observableArray<any>();
        schemaStoreCatalog = ko.observableArray<any>();
        selectedSchemaName = ko.observable<string>();
        updateSchemaLint: (any) => void = () => {};
        updateJsonLint: (any) => void = () => {};
        validationComplete: () => void;
        tokenHeader = ko.observable<string>();

        failedDetailMessage = ko.observable<string>();
        failedMessage = ko.computed<string>(() => {
            var f1 = this.schemaFailedMessage();
            var f2 = this.jsonFailedMessage();

            return (f1 || f2);
        });
        successMessage = ko.computed<string>(() => {
            return this.jsonSuccessMessage();
        });

        static defaultSchemaText = "{\n\t\"$schema\": \"https://json-schema.org/draft/2020-12/schema\"\n\}";
        static defaultJsonText = "{\n\t\n\}";

        constructor(tokenHeader, state) {
            this.tokenHeader(tokenHeader);
            this.validation = ko.validatedObservable(this);

            var schemaText = state.SchemaText || this.tryGetStorageItem("schemaText") || ValidatorViewModel.defaultSchemaText;
            var selectedSchemaName = state.SelectedSchemaName || this.tryGetStorageItem("selectedSchemaName");
            var jsonText = state.JsonText || this.tryGetStorageItem("jsonText") || ValidatorViewModel.defaultJsonText;

            if (state.Success) {
                var message = state.Message;
                if (!message && this.tryGetStorageItem("schemaText")) {
                    message = "Previous local schema loaded";
                }
                this.savingPageSuccessMessage(message);
            } else {
                this.savingPageFailedMessage(state.Message);

                schemaText = ValidatorViewModel.defaultSchemaText;
                jsonText = ValidatorViewModel.defaultJsonText;
                selectedSchemaName = "";
            }

            this.schemaText(schemaText);
            this.jsonText(jsonText);

            this.schemaText.subscribe(t => {
                this.resetState();
                if (this.selectedSchemaName() == "" && t == ValidatorViewModel.defaultSchemaText) {
                    return;
                }
                if (!this.loadingSchema()) {
                    this.selectedSchemaName("custom");
                }
            });

            this.jsonText.subscribe(() => {
                this.resetState();
            });

            this.loadCatalog(selectedSchemaName);
        }

        private async loadCatalog(selectedSchemaName: string) {
            try {
                const response = await fetch("/api/jsonschemastore/catalog", {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Csrf-Token': this.tokenHeader()
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    for (var i = 0; i < data.schemas.length; i++) {
                        var s = data.schemas[i];
                        this.schemaStoreCatalog.push(s);
                    }

                    if (selectedSchemaName) {
                        this.selectedSchemaName(selectedSchemaName);
                    }
                }
            } finally {
                this.selectedSchemaName.subscribe(async v => {
                    this.trySetStorageItem("selectedSchemaName", v);

                    if (v === "custom") {
                        return;
                    }

                    if (v) {
                        this.loadingSchema(true);
                        try {
                            const response = await fetch(v, {
                                method: "GET",
                                headers: {
                                    'X-Csrf-Token': this.tokenHeader()
                                }
                            });

                            if (response.ok) {
                                const data = await response.text();
                                this.schemaText(data);
                            }
                        } finally {
                            this.loadingSchema(false);
                        }
                    } else {
                        this.schemaText(ValidatorViewModel.defaultSchemaText);
                    }
                });
            }
        }

        private resetState() {
            this.savedUrl(null);
            this.savingPageFailedMessage(null);
            this.savingPageSuccessMessage(null);
        }

        private tryGetStorageItem(key: string): string {
            try {
                return localStorage.getItem(key);
            } catch (e) {
                return null;
            }
        }

        private trySetStorageItem(key: string, data: string): void {
            try {
                localStorage.setItem(key, data);
            } catch (e) {
            }
        }

        public initialize(): void {
            ko.computed(async () => {
                var params = { schema: this.schemaText(), json: this.jsonText() };

                this.trySetStorageItem("schemaText", this.schemaText());
                this.trySetStorageItem("jsonText", this.jsonText());

                this.schemaFailedMessage("");
                this.jsonFailedMessage("");
                this.jsonSuccessMessage("");
                this.failedDetailMessage("");
                this.schemaValidationErrors.removeAll();

                this.updateSchemaLint([]);
                this.updateJsonLint([]);

                if (!this.validation.isValid()) {
                    return;
                }

                var json = JSON.stringify(params);

                try {
                    const response = await fetch("/api/jsonschema/validate", {
                        method: "POST",
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Csrf-Token': this.tokenHeader()
                        },
                        body: json
                    });

                    if (response.ok) {
                        const result = await response.json();
                        if (result.schemaParseError || result.jsonParseError) {
                            this.handleParseErrorResult(result);
                        } else {
                            this.handleValidateResult(result);
                        }
                    } else {
                        let error: any = null;
                        try {
                            error = await response.json();
                        } catch {}
                        if (error && error.message) {
                            this.jsonFailedMessage(error.message);
                        } else {
                            this.jsonFailedMessage("Unexpected error occurred while validating JSON");
                        }
                    }
                } catch {
                    this.jsonFailedMessage("Unexpected error occurred while validating JSON");
                } finally {
                    if (this.validationComplete) {
                        this.validationComplete();
                    }
                }

            }).extend({ throttle: 400 });
        }

        handleParseErrorResult = (result) => {
            var lintAnnotations = [];
            if (result.jsonParseError) {
                this.buildLintAnnotations(lintAnnotations, [result.jsonParseError]);

                this.updateJsonLint(lintAnnotations);
                this.jsonFailedMessage("Error parsing JSON");
                this.failedDetailMessage(result.jsonParseError.message);
            }
            if (result.schemaParseError) {
                this.buildLintAnnotations(lintAnnotations, [result.schemaParseError]);

                this.updateSchemaLint(lintAnnotations);
                this.schemaFailedMessage("Error parsing schema");
                this.failedDetailMessage(result.schemaParseError.message);
            }
        };

        handleValidateResult = (result) => {
            // schema

            // don't display lint errors for resolved schemas
            var baseSchemaErrors = result.schemaErrors.filter((e) => e.schemaBaseUri === null);

            var lintAnnotations = [];
            this.buildLintAnnotations(lintAnnotations, baseSchemaErrors, "warning");

            this.updateSchemaLint(lintAnnotations);

            // validation
            for (var j = 0; j < result.validationErrors.length; j++) {
                var validationError = result.validationErrors[j];
                this.schemaValidationErrors.push(validationError);
            }

            lintAnnotations = [];
            this.buildLintAnnotations(lintAnnotations, result.validationErrors);

            this.updateJsonLint(lintAnnotations);

            if (result.valid) {
                this.jsonSuccessMessage("No errors found. JSON validates against the schema");
            } else {
                this.jsonFailedMessage("Found " + lintAnnotations.length + " error(s)");
            }
        };

        formatSchema = () => {
            this.format(
                this.schemaText(),
                result => {
                    this.updateSchemaLint([]);
                    this.schemaText(result.json);
                },
                result => {
                    this.updateSchemaLint(result);
                    this.schemaFailedMessage("Error parsing schema");
                });
        };

        formatJson = () => {
            this.format(
                this.jsonText(),
                result => {
                    this.updateJsonLint([]);
                    this.jsonText(result.json);
                },
                result => {
                    this.updateJsonLint(result);
                    this.jsonFailedMessage("Error parsing JSON");
                });
        };

        savePage = async () => {
            var params = {
                schemaText: this.schemaText(),
                selectedSchemaName: this.selectedSchemaName(),
                jsonText: this.jsonText()
            };
            var json = JSON.stringify(params);

            this.savingPageSuccessMessage(null);
            this.savingPageFailedMessage(null);
            this.savingPage(true);

            try {
                const response = await fetch("/api/jsonschema/savepage", {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Csrf-Token': this.tokenHeader()
                    },
                    body: json
                });

                if (response.ok) {
                    const result = await response.json();
                    var url = window.location.protocol + "//" + window.location.host + "/s/" + result.id;
                    this.savedUrl(url);
                } else {
                    let error: any = null;
                    try {
                        error = await response.json();
                    } catch {}
                    if (error) {
                        this.savingPageFailedMessage(error.message);
                    } else {
                        this.savingPageFailedMessage("Unexpected error occurred");
                    }
                }
            } catch {
                this.savingPageFailedMessage("Unexpected error occurred");
            } finally {
                this.savingPage(false);
            }
        };

        private async format(jsonText: string, setResult: (any) => void, setFailure: (any) => void) {
            var params = { json: jsonText };
            var json = JSON.stringify(params);

            try {
                const response = await fetch("/api/jsonschema/format", {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Csrf-Token': this.tokenHeader()
                    },
                    body: json
                });

                if (response.ok) {
                    const result = await response.json();
                    if (result.json) {
                        setResult(result);
                    } else {
                        var lintAnnotations = [];

                        this.buildLintAnnotations(lintAnnotations, [result.parseError]);

                        setFailure(lintAnnotations);
                        this.failedDetailMessage(result.parseError.message);
                    }
                } else {
                    let error: any = null;
                    try {
                        error = await response.json();
                    } catch {}
                    if (error) {
                        this.failedDetailMessage(error.message);
                    } else {
                        this.jsonFailedMessage("Unexpected error occurred while formatting JSON");
                    }
                }
            } catch {
                this.jsonFailedMessage("Unexpected error occurred while formatting JSON");
            }
        }

        public buildSchemaPath(data): string {
            var path = data.schemaId;
            if (!path) {
                return null;
            }
            if (path.indexOf('#') === -1) {
                path += '#';
            }
            path += '/' + data.errorType;

            return path;
        }

        private buildLintAnnotations(lintAnnotations, validationErrors, severity = "error") {
            for (var i = 0; i < validationErrors.length; i++) {
                var error = validationErrors[i];

                lintAnnotations.push({
                    from: (<any>CodeMirror).Pos(error.lineNumber - 1, error.linePosition - 1),
                    message: error.message,
                    severity: severity
                });

                if (error.childErrors) {
                    this.buildLintAnnotations(lintAnnotations, error.childErrors, severity);
                }
            }
        }

        schemaLint = (value, updateLinting, passOptions, cm) => {
            this.updateSchemaLint = (annotations) => {
                updateLinting(cm, annotations);
            };
        }

        jsonLint = (value, updateLinting, passOptions, cm) => {
            this.updateJsonLint = (annotations) => {
                updateLinting(cm, annotations);
            };
        }
    }
}
